import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, Calendar, ChevronLeft, ChevronRight, Check, 
  Sparkles, Moon, Sun, Cloud, Zap, Heart, Target, 
  PenLine, Clock, Save, Loader2, MessageSquare, Settings,
  LayoutDashboard, LogOut, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_URL = "/images/dea-wordmark.png"; // Small "a.gpt" logo for internal pages

// Mood options for daily check-in
const MOODS = [
  { id: 'great', emoji: '✨', label: 'Great', color: 'bg-[#87A6A7]' },
  { id: 'good', emoji: '😊', label: 'Good', color: 'bg-[#CAAB84]' },
  { id: 'okay', emoji: '😐', label: 'Okay', color: 'bg-[#AA9283]' },
  { id: 'tough', emoji: '😔', label: 'Tough', color: 'bg-[#B1897C]' },
  { id: 'difficult', emoji: '💪', label: 'Difficult', color: 'bg-[#585C4E]' }
];

// Debounce hook for auto-save
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
  
  return debouncedCallback;
}

export default function Reflections() {
  const { user, getAuthHeader, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('daily'); // 'daily' or 'weekly'
  const [reflection, setReflection] = useState(null);
  const [dailyJournal, setDailyJournal] = useState(null);
  const [history, setHistory] = useState([]);
  const [dailyHistory, setDailyHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [selectedHistoryWeek, setSelectedHistoryWeek] = useState(null);
  const [selectedDailyJournal, setSelectedDailyJournal] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dailyInspiration, setDailyInspiration] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [weeklyRes, dailyRes, historyRes, dailyHistoryRes, inspirationRes] = await Promise.all([
        axios.get(`${API}/reflections/current-week`, { headers: getAuthHeader() }),
        axios.get(`${API}/journal/today`, { headers: getAuthHeader() }),
        axios.get(`${API}/reflections/history`, { headers: getAuthHeader() }),
        axios.get(`${API}/journal/history`, { headers: getAuthHeader() }),
        axios.get(`${API}/user/daily-inspiration`, { headers: getAuthHeader() })
      ]);
      
      setReflection(weeklyRes.data);
      setDailyJournal(dailyRes.data);
      setDailyInspiration(inspirationRes.data);
      setHistory(historyRes.data.reflections || []);
      setDailyHistory(dailyHistoryRes.data.journals || []);
    } catch (error) {
      console.error('Failed to fetch reflections:', error);
      toast.error('Failed to load reflections');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save weekly reflection
  const saveWeeklyReflection = useCallback(async (data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/reflections/current-week`, data, { headers: getAuthHeader() });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save reflection:', error);
    } finally {
      setSaving(false);
    }
  }, [getAuthHeader]);

  // Auto-save daily journal
  const saveDailyJournal = useCallback(async (data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/journal/today`, data, { headers: getAuthHeader() });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save journal:', error);
    } finally {
      setSaving(false);
    }
  }, [getAuthHeader]);

  // Debounced save (3 seconds)
  const debouncedSaveWeekly = useDebounce(saveWeeklyReflection, 3000);
  const debouncedSaveDaily = useDebounce(saveDailyJournal, 3000);

  // Handle prompt response change
  const handlePromptChange = (promptId, value) => {
    const updatedPrompts = reflection.prompts.map(p => 
      p.id === promptId ? { ...p, response: value } : p
    );
    setReflection({ ...reflection, prompts: updatedPrompts });
    debouncedSaveWeekly({ prompts: updatedPrompts });
  };

  // Handle free journal change
  const handleFreeJournalChange = (value) => {
    setReflection({ ...reflection, free_journal: value });
    debouncedSaveWeekly({ free_journal: value });
  };

  // Handle daily journal changes
  const handleDailyChange = (field, value) => {
    setDailyJournal({ ...dailyJournal, [field]: value });
    debouncedSaveDaily({ [field]: value });
  };

  // Manual save
  const handleManualSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 'weekly') {
        await axios.put(`${API}/reflections/current-week`, {
          prompts: reflection.prompts,
          free_journal: reflection.free_journal,
          completed: true
        }, { headers: getAuthHeader() });
        setReflection({ ...reflection, completed: true });
      } else {
        await axios.put(`${API}/journal/today`, dailyJournal, { headers: getAuthHeader() });
      }
      setLastSaved(new Date());
      toast.success('Reflection saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Load specific week from history
  const loadHistoryWeek = async (year, weekNumber) => {
    try {
      const res = await axios.get(`${API}/reflections/week/${year}/${weekNumber}`, { headers: getAuthHeader() });
      setSelectedHistoryWeek(res.data);
    } catch (error) {
      toast.error('Failed to load reflection');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFCF7] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-[#87A6A7] animate-spin mx-auto mb-4" />
          <p className="text-[#AA9283]">Loading your reflections...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Reflections"
        description="Journal your thoughts and track your weekly reflections with Dea."
        noindex={true}
      />
      <div className="min-h-screen bg-[#FFFCF7]">
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#726158]/20 z-40"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-[#FFFCF7] border-r border-[#EAD8C2] z-50 flex flex-col"
            >
              <div className="p-4 border-b border-[#EAD8C2] flex items-center justify-between">
                <Link to="/">
                  <img src={LOGO_URL} alt="Dea" className="h-10 w-auto object-contain" />
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(false)}
                  className="text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="flex-1 p-4 overflow-y-auto">
                {(dailyInspiration?.affirmation || dailyInspiration?.scripture) ? (
                  <div className="space-y-4">
                    {dailyInspiration?.affirmation && (
                      <div className="rounded-xl p-4 bg-gradient-to-br from-[#CAAB84]/10 to-[#EAD8C2]/30 border border-[#CAAB84]/20">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="h-4 w-4 text-[#CAAB84]" />
                          <span className="text-sm font-medium text-[#726158]">Today's Affirmation</span>
                        </div>
                        <p className="text-sm text-[#726158] italic leading-relaxed">"{dailyInspiration.affirmation}"</p>
                      </div>
                    )}
                    {dailyInspiration?.scripture && (
                      <div className="rounded-xl p-4 bg-gradient-to-br from-[#87A6A7]/10 to-[#EAD8C2]/20 border border-[#87A6A7]/20">
                        <div className="flex items-center gap-2 mb-3">
                          <BookOpen className="h-4 w-4 text-[#87A6A7]" />
                          <span className="text-sm font-medium text-[#726158]">Daily Scripture</span>
                        </div>
                        <p className="text-sm text-[#726158] italic leading-relaxed">
                          "{dailyInspiration.scripture.text || dailyInspiration.scripture}"
                        </p>
                        {dailyInspiration.scripture.verse && (
                          <p className="text-xs text-[#87A6A7] mt-2 font-medium">— {dailyInspiration.scripture.verse}</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl p-4 bg-[#EAD8C2]/20 border border-[#EAD8C2] text-center">
                    <Sparkles className="h-6 w-6 text-[#CAAB84] mx-auto mb-2" />
                    <p className="text-sm text-[#AA9283]">Set your preferences in Settings to receive daily inspiration</p>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-[#EAD8C2] space-y-2">
                <Link to="/dashboard" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>
                <Link to="/chat" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Chat with Dea
                  </Button>
                </Link>
                <Link to="/reflections" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#726158] bg-[#EAD8C2]/30">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Reflections
                  </Button>
                </Link>
                <Link to="/settings" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="w-full justify-start text-[#AA9283] hover:text-[#B1897C] hover:bg-[#B1897C]/10"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-[#FFFCF7]/90 backdrop-blur-lg border-b border-[#EAD8C2] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-[#AA9283] hover:text-[#726158]"
              data-testid="sidebar-toggle-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Link to="/">
              <img src={LOGO_URL} alt="Dea" className="h-10 w-auto object-contain" />
            </Link>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#87A6A7]/10 border border-[#87A6A7]/20">
              <BookOpen className="h-4 w-4 text-[#87A6A7]" />
              <span className="text-sm font-medium text-[#87A6A7]">Reflections</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Save Status */}
            <div className="flex items-center gap-2 text-sm text-[#AA9283] mr-4">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : lastSaved ? (
                <>
                  <Check className="h-4 w-4 text-[#87A6A7]" />
                  <span>Saved</span>
                </>
              ) : null}
            </div>
            <Link to="/chat">
              <Button variant="ghost" size="sm" className="text-[#AA9283]">
                <MessageSquare className="h-4 w-4 mr-2" />
                Chat
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('daily')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'daily'
                ? 'bg-[#CAAB84] text-white shadow-lg'
                : 'bg-[#EAD8C2]/50 text-[#AA9283] hover:bg-[#EAD8C2]'
            }`}
          >
            <Sun className="h-5 w-5" />
            Daily Check-in
          </button>
          <button
            onClick={() => setActiveTab('weekly')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'weekly'
                ? 'bg-[#87A6A7] text-white shadow-lg'
                : 'bg-[#EAD8C2]/50 text-[#AA9283] hover:bg-[#EAD8C2]'
            }`}
          >
            <Calendar className="h-5 w-5" />
            Weekly Reflection
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <AnimatePresence mode="wait">
              {activeTab === 'weekly' && reflection && (
                <motion.div
                  key="weekly"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  {/* Week Header */}
                  <div className="glass-card rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h1 className="font-serif text-2xl text-[#726158]">
                        Journey Week {reflection.journey_week || 1}
                      </h1>
                      {reflection.completed && (
                        <span className="px-3 py-1 rounded-full bg-[#87A6A7]/20 text-[#87A6A7] text-sm">
                          ✓ Completed
                        </span>
                      )}
                    </div>
                    <p className="text-[#AA9283]">
                      {reflection.start_date} — {reflection.end_date}
                    </p>
                    {reflection.cycle_phase && (
                      <div className="mt-3 flex items-center gap-2">
                        <Moon className="h-4 w-4 text-[#B1897C]" />
                        <span className="text-sm text-[#B1897C]">{reflection.cycle_phase} Phase</span>
                      </div>
                    )}
                  </div>

                  {/* Prompts */}
                  {reflection.prompts.map((prompt, index) => (
                    <motion.div
                      key={prompt.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="glass-card rounded-2xl p-6"
                    >
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          index === 0 ? 'bg-[#87A6A7]/20' :
                          index === 1 ? 'bg-[#CAAB84]/20' :
                          index === 2 ? 'bg-[#B1897C]/20' :
                          'bg-[#AA9283]/20'
                        }`}>
                          {index === 0 ? <Sparkles className="h-5 w-5 text-[#87A6A7]" /> :
                           index === 1 ? <Zap className="h-5 w-5 text-[#CAAB84]" /> :
                           index === 2 ? <Heart className="h-5 w-5 text-[#B1897C]" /> :
                           <Target className="h-5 w-5 text-[#AA9283]" />}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-[#726158]">{prompt.question}</h3>
                        </div>
                      </div>
                      <Textarea
                        value={prompt.response}
                        onChange={(e) => handlePromptChange(prompt.id, e.target.value)}
                        placeholder="Take your time to reflect..."
                        className="min-h-[120px] bg-[#FFFCF7] border-[#EAD8C2] text-[#726158] placeholder:text-[#B1897C] resize-none focus:border-[#87A6A7] focus:ring-[#87A6A7]/20"
                        data-testid={`prompt-${prompt.id}`}
                      />
                    </motion.div>
                  ))}

                  {/* Free Journal */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="glass-card rounded-2xl p-6"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#585C4E]/20 flex items-center justify-center">
                        <PenLine className="h-5 w-5 text-[#585C4E]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-[#726158]">Free Journal</h3>
                        <p className="text-sm text-[#B1897C]">Anything else on your mind...</p>
                      </div>
                    </div>
                    <Textarea
                      value={reflection.free_journal}
                      onChange={(e) => handleFreeJournalChange(e.target.value)}
                      placeholder="Write freely here..."
                      className="min-h-[150px] bg-[#FFFCF7] border-[#EAD8C2] text-[#726158] placeholder:text-[#B1897C] resize-none focus:border-[#87A6A7] focus:ring-[#87A6A7]/20"
                      data-testid="free-journal"
                    />
                  </motion.div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleManualSave}
                      disabled={saving}
                      className="btn-gold"
                      data-testid="save-reflection-btn"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Reflection
                    </Button>
                  </div>
                </motion.div>
              )}

              {activeTab === 'daily' && dailyJournal && (
                <motion.div
                  key="daily"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  {/* Today Header */}
                  <div className="glass-card rounded-2xl p-6">
                    <h1 className="font-serif text-2xl text-[#726158] mb-2">
                      Daily Check-in
                    </h1>
                    <p className="text-[#AA9283]">
                      {new Date().toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>

                  {/* Mood Selection */}
                  <div className="glass-card rounded-2xl p-6">
                    <h3 className="font-medium text-[#726158] mb-4">How are you feeling today?</h3>
                    <div className="flex gap-3 flex-wrap">
                      {MOODS.map((mood) => (
                        <button
                          key={mood.id}
                          onClick={() => handleDailyChange('mood', mood.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                            dailyJournal.mood === mood.id
                              ? `${mood.color} text-white shadow-lg scale-105`
                              : 'bg-[#EAD8C2]/50 text-[#AA9283] hover:bg-[#EAD8C2]'
                          }`}
                        >
                          <span className="text-lg">{mood.emoji}</span>
                          <span>{mood.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Energy Level */}
                  <div className="glass-card rounded-2xl p-6">
                    <h3 className="font-medium text-[#726158] mb-4">Energy Level</h3>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <button
                          key={level}
                          onClick={() => handleDailyChange('energy_level', level)}
                          className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-medium transition-all ${
                            dailyJournal.energy_level === level
                              ? 'bg-[#CAAB84] text-white shadow-lg scale-105'
                              : 'bg-[#EAD8C2]/50 text-[#AA9283] hover:bg-[#EAD8C2]'
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                    <p className="text-sm text-[#B1897C] mt-2">1 = Low, 5 = High</p>
                  </div>

                  {/* Gratitude */}
                  <div className="glass-card rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Heart className="h-5 w-5 text-[#B1897C]" />
                      <h3 className="font-medium text-[#726158]">What are you grateful for today?</h3>
                    </div>
                    <Textarea
                      value={dailyJournal.gratitude}
                      onChange={(e) => handleDailyChange('gratitude', e.target.value)}
                      placeholder="One thing you're grateful for..."
                      className="min-h-[80px] bg-[#FFFCF7] border-[#EAD8C2] text-[#726158] placeholder:text-[#B1897C] resize-none focus:border-[#87A6A7]"
                      data-testid="daily-gratitude"
                    />
                  </div>

                  {/* Quick Note */}
                  <div className="glass-card rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <PenLine className="h-5 w-5 text-[#585C4E]" />
                      <h3 className="font-medium text-[#726158]">Quick Note</h3>
                    </div>
                    <Textarea
                      value={dailyJournal.quick_note}
                      onChange={(e) => handleDailyChange('quick_note', e.target.value)}
                      placeholder="Anything on your mind today..."
                      className="min-h-[100px] bg-[#FFFCF7] border-[#EAD8C2] text-[#726158] placeholder:text-[#B1897C] resize-none focus:border-[#87A6A7]"
                      data-testid="daily-note"
                    />
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleManualSave}
                      disabled={saving}
                      className="btn-gold"
                      data-testid="save-daily-btn"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Check-in
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar - History */}
          <div className="space-y-6">
            {activeTab === 'weekly' ? (
              <div className="glass-card rounded-2xl p-6">
                <h2 className="font-serif text-lg text-[#726158] mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#AA9283]" />
                  Reflection History
                </h2>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2 pr-2">
                    {history.length === 0 ? (
                      <p className="text-[#B1897C] text-sm text-center py-8">
                        Your past reflections will appear here
                      </p>
                    ) : (
                      history.map((ref) => (
                        <button
                          key={`${ref.year}-${ref.week_number}`}
                          onClick={() => loadHistoryWeek(ref.year, ref.week_number)}
                          className={`w-full text-left p-3 rounded-xl transition-colors ${
                            selectedHistoryWeek?.week_number === ref.week_number && 
                            selectedHistoryWeek?.year === ref.year
                              ? 'bg-[#87A6A7]/10 border border-[#87A6A7]/30'
                              : 'bg-[#EAD8C2]/30 hover:bg-[#EAD8C2]/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-[#726158]">
                              Journey Week {ref.journey_week || 1}
                            </span>
                            {ref.completed && (
                              <Check className="h-4 w-4 text-[#87A6A7]" />
                            )}
                          </div>
                          <p className="text-xs text-[#B1897C]">
                            {ref.start_date} — {ref.end_date}
                          </p>
                          {ref.cycle_phase && (
                            <p className="text-xs text-[#AA9283] mt-1">{ref.cycle_phase} Phase</p>
                          )}
                          {ref.prompts?.[0]?.response && (
                            <p className="text-xs text-[#AA9283] mt-2 line-clamp-2">
                              {ref.prompts[0].response.slice(0, 80)}...
                            </p>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            ) : (
              <div className="glass-card rounded-2xl p-6">
                <h2 className="font-serif text-lg text-[#726158] mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#AA9283]" />
                  Check-in History
                </h2>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2 pr-2">
                    {dailyHistory.length === 0 ? (
                      <p className="text-[#B1897C] text-sm text-center py-8">
                        Your daily check-ins will appear here
                      </p>
                    ) : (
                      dailyHistory.map((journal) => (
                        <button
                          key={journal.date}
                          onClick={() => setSelectedDailyJournal(journal)}
                          className={`w-full text-left p-3 rounded-xl transition-colors ${
                            selectedDailyJournal?.date === journal.date
                              ? 'bg-[#CAAB84]/20 border border-[#CAAB84]/30'
                              : 'bg-[#EAD8C2]/30 hover:bg-[#EAD8C2]/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-[#726158]">
                              {new Date(journal.date).toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </span>
                            {journal.mood && (
                              <span className="text-lg">
                                {MOODS.find(m => m.id === journal.mood)?.emoji}
                              </span>
                            )}
                          </div>
                          {journal.energy_level && (
                            <p className="text-xs text-[#B1897C]">
                              Energy: {journal.energy_level}/5
                            </p>
                          )}
                          {(journal.gratitude || journal.quick_note) && (
                            <p className="text-xs text-[#AA9283] mt-1 line-clamp-2">
                              {(journal.gratitude || journal.quick_note).slice(0, 60)}...
                            </p>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* View Selected Weekly Reflection History */}
            {selectedHistoryWeek && activeTab === 'weekly' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-2xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-[#726158]">
                    Journey Week {selectedHistoryWeek.journey_week || 1} Details
                  </h3>
                  <button
                    onClick={() => setSelectedHistoryWeek(null)}
                    className="text-[#B1897C] hover:text-[#726158]"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-3">
                  {selectedHistoryWeek.prompts?.map((prompt) => (
                    <div key={prompt.id}>
                      <p className="text-xs text-[#AA9283] mb-1">{prompt.question}</p>
                      <p className="text-sm text-[#726158]">
                        {prompt.response || <em className="text-[#B1897C]">No response</em>}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* View Selected Daily Check-in History */}
            {selectedDailyJournal && activeTab === 'daily' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-2xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-[#726158]">
                    {new Date(selectedDailyJournal.date).toLocaleDateString('en-US', { 
                      weekday: 'long',
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </h3>
                  <button
                    onClick={() => setSelectedDailyJournal(null)}
                    className="text-[#B1897C] hover:text-[#726158]"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-4">
                  {/* Mood */}
                  {selectedDailyJournal.mood && (
                    <div>
                      <p className="text-xs text-[#AA9283] mb-1">How were you feeling?</p>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">
                          {MOODS.find(m => m.id === selectedDailyJournal.mood)?.emoji}
                        </span>
                        <span className="text-sm font-medium text-[#726158]">
                          {MOODS.find(m => m.id === selectedDailyJournal.mood)?.label}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Energy Level */}
                  {selectedDailyJournal.energy_level && (
                    <div>
                      <p className="text-xs text-[#AA9283] mb-1">Energy Level</p>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium ${
                              level <= selectedDailyJournal.energy_level
                                ? 'bg-[#CAAB84] text-white'
                                : 'bg-[#EAD8C2]/50 text-[#AA9283]'
                            }`}
                          >
                            {level}
                          </div>
                        ))}
                        <span className="text-sm text-[#726158] ml-2">
                          {selectedDailyJournal.energy_level}/5
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Gratitude */}
                  {selectedDailyJournal.gratitude && (
                    <div>
                      <p className="text-xs text-[#AA9283] mb-1">What were you grateful for?</p>
                      <p className="text-sm text-[#726158]">
                        {selectedDailyJournal.gratitude}
                      </p>
                    </div>
                  )}
                  
                  {/* Quick Note */}
                  {selectedDailyJournal.quick_note && (
                    <div>
                      <p className="text-xs text-[#AA9283] mb-1">Quick Note</p>
                      <p className="text-sm text-[#726158]">
                        {selectedDailyJournal.quick_note}
                      </p>
                    </div>
                  )}
                  
                  {/* No content message */}
                  {!selectedDailyJournal.mood && 
                   !selectedDailyJournal.energy_level && 
                   !selectedDailyJournal.gratitude && 
                   !selectedDailyJournal.quick_note && (
                    <p className="text-sm text-[#B1897C] italic">
                      No details recorded for this day
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </main>
    </div>
    </>
  );
}
